import React, { useCallback, useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router-dom'
import _ from 'lodash'
import dayjs from 'dayjs'
import { DevicePhoneMobileIcon } from '@heroicons/react/20/solid'
import {
  CurrencyDollarIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

// Components
import { AddExhibitorModal } from '../../components/AddExhibitorModal'
import { AssignLicenseModal } from '../../components/AssignLicenseModal'
import { Button } from '../../components/Button'
import { CircleCheckIconSolid } from '../../components/CircleCheckIconSolid'
import { DataTable } from '../../components/DataTable'
import { DataTile } from '../../components/DataTile'
import { Dialog } from '../../components/Dialog'
import { EditIcon } from '../../components/EditIcon'
import { EventHeader } from '../../components/EventHeader'
import { IdentificationIcon } from '../../components/IdentificationIcon'
import { LicenseQRModal } from '../../components/LicenseQRModal'
import { LicenseStatusBadge } from '../../components/LicenseStatusBadge'
import { LicenseTypeBadge } from '../../components/LicenseTypeBadge'
import { Modal } from '../../components/Modal'
import { PhoneNumberInput } from '../../components/PhoneNumberInput'
import { PurchaseLicensesModal } from '../../components/PurchaseLicensesModal'
import { PurchaseReceipts } from '../../components/PurchaseReceipts'
import { RefundModal } from '../../components/RefundModal'
import { QRIcon } from '../../components/QRIcon'
import { ResendLicenseEmailsModal } from '../../components/ResendLicenseEmailsModal'
import { TextInput } from '../../components/TextInput'
import { Toggle } from '../../components/Toggle'
import { StateContainer } from '../../components/StateContainer'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'
import { UserStoreContext } from '../../stores/UserStore'

// Service
import { getExhibitor, getExhibitorKPI, updateExhibitor } from '../../services/exhibitors.service'
import { getOrganizationUsers, updateOrganization } from '../../services/organizations.service'
import {
  cancelLicense,
  getLicenses,
  resendLicenseEmails,
  updateLicense,
} from '../../services/licenses.service'
import { cancelRentalDevice, getRentalDevices, issueRefund } from '../../services/refunds.service'

// Utils & Styles
import { getChargeData, getPricingData } from '../../components/PurchaseLicensesModal/helpers'

// Hooks
import usePurchaseTransaction from '../../hooks/PurchaseTransaction'
import { toast } from '../../utils/helpers'

// Licenses are bought per exhibitor and stay well inside this bound; it exists so
// the totals fetch can't silently truncate the way a default page would.
const ALL_LICENSES_LIMIT = 200

// Split rather than appended to, so the destructive variant doesn't carry the purple
// utilities alongside its own — which of those won would come down to stylesheet order
const ROW_ACTION_BASE =
  'flex flex-row items-center gap-1 whitespace-nowrap px-2 py-0.5 font-bold hover:rounded-full'
const ROW_ACTION_CLASS = `${ROW_ACTION_BASE} text-purple hover:bg-status-purple`
const ROW_ACTION_DESTRUCTIVE_CLASS = `${ROW_ACTION_BASE} text-error-dark hover:bg-error-light`

/**
 *
 * EventExhibitorDetail
 *
 */
const EventExhibitorDetail = observer(() => {
  // Context
  const { event, eventId, updateActiveEntity } = useContext(NavigationStoreContext)
  const { isEEUser } = useContext(UserStoreContext)
  const { eventExhibitorId } = useParams()
  const navigate = useNavigate()

  // Hooks
  const { loadingPurchase, submitPurchase, repeatPurchaseDialog } = usePurchaseTransaction()

  // State
  const [loadingExhibitor, setLoadingExhibitor] = useState(false)
  const [eventExhibitor, setEventExhibitor] = useState(null)
  const [kpis, setKpis] = useState(null)
  const [loadingAssigned, setLoadingAssigned] = useState(false)
  const [loadingUnassigned, setLoadingUnassigned] = useState(false)
  const [licenses, setLicenses] = useState([])
  const [assignedLicenses, setAssignedLicenses] = useState([])
  const [unassignedLicenses, setUnassignedLicenses] = useState([])
  const [showExhibitorModal, setShowExhibitorModal] = useState(false)
  const [editExhibitor, setEditExhibitor] = useState(false)
  const [tasks, setTasks] = useState(null)
  const [domainError, setDomainError] = useState(null)
  const [domain, setDomain] = useState('')
  const [qrLicense, setQrLicense] = useState(null)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [selectedLicenses, setSelectedLicenses] = useState([])
  const [clearSelectedRows, setClearSelectedRows] = useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [licenseToAssign, setLicenseToAssign] = useState(null)
  // The license a cancel is pending confirmation for: `{ license, type }`. Refunds go
  // through their own modal, since one action can cover several licenses and devices.
  const [licenseToResolve, setLicenseToResolve] = useState(null)
  const [resolvingLicense, setResolvingLicense] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [refundSelection, setRefundSelection] = useState([])
  const [issuingRefund, setIssuingRefund] = useState(false)
  const [rentalDevices, setRentalDevices] = useState([])
  const [loadingRentalDevices, setLoadingRentalDevices] = useState(false)
  const [deviceToCancel, setDeviceToCancel] = useState(null)
  const [cancellingDevice, setCancellingDevice] = useState(false)
  const [assignee, setAssignee] = useState(null)
  // The role id an assignment is waiting on a phone number for, or null (EXEC-1608)
  const [pendingAssigneeId, setPendingAssigneeId] = useState(null)
  const [assignableUsers, setAssignableUsers] = useState([])
  const [assignSearchTerm, setAssignSearchTerm] = useState('')
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false)
  const [updatingLicense, setUpdatingLicense] = useState(false)

  // Pagination — each licenses table pages independently against the API,
  // filtered by `is_assigned`, so their page counts stay accurate
  const [assignedPage, setAssignedPage] = useState(1)
  const [assignedTotal, setAssignedTotal] = useState(0)
  const [assignedPerPage, setAssignedPerPage] = useState(20)
  const [unassignedPage, setUnassignedPage] = useState(1)
  const [unassignedTotal, setUnassignedTotal] = useState(0)
  const [unassignedPerPage, setUnassignedPerPage] = useState(20)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  // Drives the phone-number prompt shown when assigning to someone we hold no number for
  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors },
    reset: resetPhone,
  } = useForm({ defaultValues: { phoneNumber: '' } })

  const LICENSES_URL = `/events/${eventId}/exhibitors/${eventExhibitorId}/licenses/`

  // Section header counts. Cancelled licenses are excluded — they're still listed
  // in the tables so their status is visible, but they aren't licenses the
  // exhibitor can use. A refunded license that wasn't cancelled still counts, since
  // its assignee carries on using it. Counted across every license, not one page.
  const activeLicenses = licenses.filter((license) => !license.isCancelled)
  const assignedCount = activeLicenses.filter((license) => license.assignee !== null).length
  const unassignedCount = activeLicenses.length - assignedCount

  /**
   * Gets the exhibitor and KPIs.
   */
  const getUpdatedExhibitor = async () => {
    const response = await getExhibitor(
      eventId,
      eventExhibitorId,
      handleErrors,
      () => {},
      () => {},
    )
    const exhibitorKPI = await getExhibitorKPI(
      eventId,
      eventExhibitorId,
      handleErrors,
      setLoadingExhibitor,
    )
    setKpis(exhibitorKPI)
    setEventExhibitor(response)
  }

  /**
   * Builds a paged licenses URL for one of the two tables.
   * @param {boolean} isAssigned which table the page is for
   * @param {number} limit rows per page
   * @param {number} page 1-indexed page number
   */
  const licensesPageUrl = (isAssigned, limit, page) =>
    `${LICENSES_URL}?expand=assignee&is_assigned=${isAssigned}&limit=${limit}&page=${page}`

  /**
   * Gets a page of assigned licenses. Refunded licenses stay listed so their
   * derived status is still visible.
   * @param {string} url
   */
  const getAssignedLicenses = async (url) => {
    const response = await getLicenses(url, handleErrors, setLoadingAssigned)

    if (response) {
      setAssignedTotal(response.count)
      setAssignedLicenses(response.results)
    }
  }

  /**
   * Gets a page of unassigned licenses.
   * @param {string} url
   */
  const getUnassignedLicenses = async (url) => {
    const response = await getLicenses(url, handleErrors, setLoadingUnassigned)

    if (response) {
      setUnassignedTotal(response.count)
      setUnassignedLicenses(response.results)
    }
  }

  /**
   * Gets every license for this exhibitor.
   *
   * The assigned/unassigned header count and the purchase modal's pricing tiers
   * are totals across all licenses, not a page, so they can't be derived from
   * either table's current page.
   */
  const getAllLicenses = async () => {
    // `expand=assignee` matters: unexpanded, the API returns `assignee` as a bare id,
    // and the assignee picker below reads `assignee.id` off these rows
    const response = await getLicenses(
      `${LICENSES_URL}?expand=assignee&limit=${ALL_LICENSES_LIMIT}`,
      handleErrors,
    )

    if (response) setLicenses(response.results)
  }

  /**
   * Reloads both tables and the totals.
   *
   * Assigning or unassigning moves a license from one table to the other, so
   * both reset to the first page rather than risk landing on a page that no
   * longer exists.
   */
  const refreshLicenses = () => {
    setAssignedPage(1)
    setUnassignedPage(1)
    getAssignedLicenses(licensesPageUrl(true, assignedPerPage, 1))
    getUnassignedLicenses(licensesPageUrl(false, unassignedPerPage, 1))
    getAllLicenses()
  }

  /**
   * Handles submitting the edit exhibitor form.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    // eslint-disable-next-line no-param-reassign
    if (data.numFreeLicensesOverride === '') data.numFreeLicensesOverride = null
    const response = await updateExhibitor(
      eventId,
      data,
      handleErrors,
      setLoadingExhibitor,
      (m) => {
        handleSuccess(m)
        setShowExhibitorModal(false)
      },
    )
    setEventExhibitor(response)
  }

  /**
   * Handles submitting the EE-side purchase form with adjustable pricing.
   * @param {object} data
   */
  const submitPurchasingForm = async (data) => {
    const pricingInfo = getPricingData(event)
    // The licenses this exhibitor bought, computed by the API across the whole set
    // rather than off the loaded rows, which stop at the first 200
    const chargeData = await getChargeData(
      data,
      pricingInfo,
      eventExhibitor?.licenseCounts?.numValidPaidLicenses || 0,
      true,
    )

    await submitPurchase(eventId, eventExhibitorId, chargeData, handleErrors, () => {
      handleSuccess('Licenses purchased successfully')
      setShowPurchaseModal(false)
      getUpdatedExhibitor()
      refreshLicenses()
    })
  }

  /**
   * Handles resending the selected standardized emails to the selected licensed users.
   * @param {array} identifiers email template identifiers to send
   */
  const submitResendEmails = async (identifiers) => {
    const response = await resendLicenseEmails(
      eventId,
      eventExhibitorId,
      { licenseIds: selectedLicenses.map((license) => license.id), identifiers },
      handleErrors,
      setLoadingResend,
    )

    if (response) {
      handleSuccess(
        `Emails sent to ${selectedLicenses.length} ${
          selectedLicenses.length === 1 ? 'user' : 'users'
        }.`,
      )
      setShowResendModal(false)
      setSelectedLicenses([])
      setClearSelectedRows((toggled) => !toggled)
    }
  }

  /**
   * Loads the exhibitor's staff who are eligible to hold this license.
   *
   * Staff already holding another active license on this exhibitor are excluded,
   * since the API enforces one license per assignee per exhibitor. The license's
   * own current assignee stays in the list so it can render as the selected value.
   */
  const getAssignableUsers = async () => {
    if (!eventExhibitor || !licenseToAssign) return

    const baseUrl = `/organizations/${eventExhibitor.exhibitor.id}/roles/?expand=user&limit=50`
    const response = await getOrganizationUsers(
      assignSearchTerm ? `${baseUrl}&q=${assignSearchTerm}` : baseUrl,
      handleErrors,
      setLoadingAssignableUsers,
      () => {},
    )

    if (!response) return

    // Built from every license, not the assigned table's current page, or staff
    // holding a license on another page would wrongly appear selectable.
    // A cancelled license doesn't count against its assignee, so those staff stay
    // selectable — matches the API's one-usable-license-per-assignee rule. A refunded
    // license still holds its assignee's slot, since it's still usable.
    const unavailable = new Set(
      licenses
        .filter(
          (license) =>
            license.assignee && !license.isCancelled && license.id !== licenseToAssign.id,
        )
        .map((license) => license.assignee.id),
    )

    setAssignableUsers(
      response.results
        .filter((role) => !unavailable.has(role.id))
        .map((role) => ({
          label: `${role.user.firstName} ${role.user.lastName}`,
          id: role.id,
          // Carried so the assign flow knows whether to ask for one (EXEC-1608)
          phoneNumber: role.user.phoneNumber,
        })),
    )
  }

  /**
   * Assigns, reassigns or unassigns the selected license.
   * @param {string|null} assigneeId organization role id, or null to unassign
   * @param {string|null} phoneNumber phone to set on the assignee, if one was collected
   */
  const performAssignment = async (assigneeId, phoneNumber = null) => {
    const payload = { assignee: assigneeId }

    if (phoneNumber) payload.assignee_phone_number = phoneNumber

    const response = await updateLicense(
      eventId,
      eventExhibitorId,
      licenseToAssign.id,
      payload,
      handleErrors,
      setUpdatingLicense,
      handleSuccess,
    )

    if (response) {
      setLicenseToAssign(null)
      setAssignee(null)
      setPendingAssigneeId(null)
      resetPhone({ phoneNumber: '' })
      // A license can move between the two tables, so drop any resend selection
      // rather than leave it pointing at a row that's no longer listed
      setSelectedLicenses([])
      setClearSelectedRows((toggled) => !toggled)
      refreshLicenses()
    }
  }

  /**
   * Assigns the license, pausing to collect a phone number where we hold none.
   *
   * EXEC-1598 surfaced assignees' phone and email on this page with an empty state for
   * the ones we're missing; this closes the hole that produces them, by asking at the
   * point EE assigns (EXEC-1608). Mirrors the exhibitor-side prompt, Skip included — EE
   * shouldn't be stuck when a booth staffer genuinely has no number to give.
   * @param {string|null} assigneeId organization role id, or null to unassign
   */
  const submitAssignment = async (assigneeId) => {
    // Nothing to collect when unassigning, or when we already hold a number
    if (!assigneeId || assignee?.phoneNumber) {
      await performAssignment(assigneeId)
      return
    }

    setPendingAssigneeId(assigneeId)
  }

  /**
   * Loads the exhibitor's rented devices, which are refunded and cancelled like licenses.
   */
  const getUpdatedRentalDevices = async () => {
    const response = await getRentalDevices(
      eventId,
      eventExhibitorId,
      handleErrors,
      setLoadingRentalDevices,
    )

    if (response) setRentalDevices(response.results)
  }

  /**
   * Refunds everything the modal had selected.
   *
   * A selection spanning two orders is two gateway credits, and either can be refused on
   * its own — an order that hasn't settled, most often. Both halves of the answer are
   * reported: money that did go back can't be undone because the rest didn't.
   * @param {object} selection `{ licenseIds, rentalDeviceIds, internalNote }`
   */
  const submitRefund = async (selection) => {
    const response = await issueRefund(
      eventId,
      eventExhibitorId,
      selection,
      handleErrors,
      setIssuingRefund,
    )

    if (!response) return

    setShowRefundModal(false)
    setRefundSelection([])

    const refunded = response.refunds.reduce((sum, refund) => sum + parseFloat(refund.amount), 0)
    handleSuccess(
      `Refunded $${refunded.toFixed(2)} across ${response.refunds.length} ${
        response.refunds.length === 1 ? 'order' : 'orders'
      }.`,
    )

    // Surfaced separately from the success: this half genuinely didn't happen, and
    // rolling the whole action back was never an option once a credit had gone through
    response.failed.forEach((failure) =>
      handleErrors(`Order ${failure.shortId} was not refunded: ${failure.detail}`),
    )

    setSelectedLicenses([])
    setClearSelectedRows((toggled) => !toggled)
    getUpdatedExhibitor()
    refreshLicenses()
    getUpdatedRentalDevices()
  }

  /**
   * Cancels the rented device awaiting confirmation.
   */
  const resolveDeviceCancel = async () => {
    const response = await cancelRentalDevice(
      eventId,
      eventExhibitorId,
      deviceToCancel.id,
      handleErrors,
      setCancellingDevice,
      handleSuccess,
    )

    setDeviceToCancel(null)

    if (response) {
      getUpdatedExhibitor()
      getUpdatedRentalDevices()
    }
  }

  /**
   * Cancels the license awaiting confirmation.
   *
   * Cancel is independent of refunding (EXEC-1544) — it ends the license without
   * returning any money, and refunding leaves it working — so both are offered on every
   * license until it has already had that particular action applied.
   */
  const resolveLicense = async () => {
    const { license } = licenseToResolve

    const response = await cancelLicense(
      eventId,
      eventExhibitorId,
      license.id,
      handleErrors,
      setResolvingLicense,
      handleSuccess,
    )

    setLicenseToResolve(null)

    if (response) {
      // Cancelling drops a license out of the active counts and can free its assignee
      // up to hold another, so the tables and the exhibitor totals both need rereading
      setSelectedLicenses([])
      setClearSelectedRows((toggled) => !toggled)
      getUpdatedExhibitor()
      refreshLicenses()
    }
  }

  /**
   * Spells out what the pending cancel will do before it's confirmed.
   *
   * Cancelling releases the assignee (EXEC-1600), which is a consequence worth naming —
   * EE is unassigning someone as well as ending a license, and that person becomes free
   * to hold another.
   * @param {object} pending `{ license }`
   */
  const resolveLicenseMessage = ({ license }) => {
    const released = license.assignee
      ? ` ${license.assignee.user.firstName} ${license.assignee.user.lastName} will be unassigned from it and free to hold another.`
      : ''

    return `License ${license.licenseId} will stop working and no longer count towards this exhibitor's licenses.${released} This doesn't refund anything.`
  }

  /**
   * Renders the refund and cancel actions for a license row.
   *
   * Each is offered until it has already been applied, so a license can be refunded
   * and later cancelled, or either on its own. Refund is hidden where the API would
   * reject it anyway — a free license, or one with no purchase recorded against it.
   * @param {object} license
   */
  const renderRefundAction = (license) => {
    // Nothing to give back on a comped license or one already refunded, and a license
    // with no transaction behind it was never paid for
    const isRefundable = !license.isRefunded && !license.isFreeLicense && !!license.transaction
    if (!isEEUser || !isRefundable) return null

    return (
      <button
        className={ROW_ACTION_CLASS}
        onClick={() => openRefundModal([`license:${license.id}`])}
        type="button"
      >
        <CurrencyDollarIcon className="h-4 shrink-0 stroke-purple" />
        Refund
      </button>
    )
  }

  /**
   * Opens the refund modal with `preselected` already ticked.
   *
   * A row's Refund button is a selection of one — the modal is the same either way, so
   * EE can add the other license they meant to include without starting over.
   * @param {array} preselected modal item keys
   */
  const openRefundModal = (preselected = []) => {
    setRefundSelection(preselected)
    setShowRefundModal(true)
  }

  const renderCancelAction = (license) => {
    if (!isEEUser || license.isCancelled) return null

    return (
      <button
        className={ROW_ACTION_DESTRUCTIVE_CLASS}
        onClick={() => setLicenseToResolve({ license, type: 'cancel' })}
        type="button"
      >
        <XCircleIcon className="h-4 shrink-0 stroke-error-dark" />
        Cancel
      </button>
    )
  }

  /**
   * Builds a fixed-width column holding a single row action.
   *
   * Actions used to share one cell, which centred whatever that row happened to have —
   * so a row without a Refund pulled its Assign and Cancel out of line with the rows
   * that had one. A column each gives every action a fixed slot, and a row that can't
   * use one leaves a gap in line with the rows that can.
   * @param {string} id
   * @param {func} cell
   * @param {string} width
   */
  const actionColumn = (id, cell, width) => ({
    id,
    name: '',
    cell,
    center: true,
    width,
  })

  /**
   * The Free/Paid column, shown on both licenses tables.
   */
  const typeColumn = {
    id: 'type',
    grow: 0.5,
    name: 'Type',
    selector: (row) => row.isFreeLicense,
    cell: (row) => <LicenseTypeBadge isFreeLicense={row.isFreeLicense} />,
    minWidth: '100px',
  }

  const debounceAssignSearch = useCallback(_.debounce(setAssignSearchTerm, 650), [])

  /**
   * Drops any pending search debounce so it can't fire after unmount.
   */
  useEffect(() => () => debounceAssignSearch.cancel(), [])

  /**
   * Loads assignable staff whenever the modal opens or its search term changes.
   */
  useEffect(() => {
    getAssignableUsers()
  }, [licenseToAssign, assignSearchTerm])

  /**
   * Loads exhibitor details.
   */
  useEffect(() => {
    getUpdatedExhibitor()
  }, [])

  useEffect(() => {
    if (eventExhibitor?.exhibitor.allowedDomain) setDomain(eventExhibitor.exhibitor.allowedDomain)
  }, [eventExhibitor])

  /**
   * Loads both licenses tables and the totals.
   */
  useEffect(() => {
    if (eventExhibitorId) {
      refreshLicenses()
      getUpdatedRentalDevices()
    }
  }, [eventExhibitorId])

  /**
   * Loads exhibitor tasks.
   */
  useEffect(() => {
    if (eventExhibitor?.tasklistItems) {
      const completedTasks = _.filter(
        _.map(eventExhibitor.tasklistItems, (i) => i),
        (i) => i.isComplete,
      )
      const lastCompleted = _.reduce(completedTasks, (a, b) => {
        if (a.isComplete && b.isComplete) {
          return dayjs(a.completedAt).isAfter(dayjs(b.completedAt)) ? a : b
        }
        if (a.isComplete) {
          return a
        }
        return b
      })
      const taskCount = eventExhibitor.tasklistItems.length
      setTasks({
        completed: completedTasks.length,
        lastCompleted,
        total: taskCount,
      })
    }
  }, [eventExhibitor])

  return (
    <div className="h-full w-full">
      <StateContainer loading={loadingExhibitor}>
        <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-3">
          <EventHeader event={event} />

          <div className="flex items-center justify-between">
            <span className="text-lg font-bold lg:text-2xl">
              {eventExhibitor?.exhibitor?.name}
            </span>

            <div className="flex gap-2">
              {isEEUser && (
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  icon={<CurrencyDollarIcon className="h-4 stroke-white sm:h-5" />}
                  label="Purchase"
                  onClick={() => setShowPurchaseModal(true)}
                />
              )}
              <Button
                background="bg-white"
                icon={<EditIcon className="h-3 fill-gray sm:h-4" />}
                label="Edit"
                onClick={() => {
                  setEditExhibitor({
                    ...eventExhibitor,
                    exhibitor: eventExhibitor.exhibitor.name,
                    numFreeLicenses: eventExhibitor.numFreeLicenses || 0,
                  })
                  setShowExhibitorModal(true)
                }}
                outlined
              />
              <Button
                background="bg-white"
                label="View"
                onClick={() => {
                  updateActiveEntity('exhibitor', eventExhibitor?.exhibitor?.id)
                  navigate(`/exhibitor/${eventExhibitor?.exhibitor?.id}/event/${eventId}/home`)
                }}
                outlined
              />
            </div>
          </div>

          {/* Tasks */}
          <div>
            <span className="text-md mr-2 font-bold">Tasks Completed:</span>

            {tasks && (
              <span className="font-bold">
                {tasks.completed}/{tasks.total}
              </span>
            )}

            {tasks?.lastCompleted && (
              <>
                <div className="mb-1">Last task completed:</div>
                <span className="mt-2 inline-flex items-center gap-2 rounded-full border-2 border-purple py-2 pl-3 pr-16">
                  <CircleCheckIconSolid className="h-4 w-6 fill-purple" />
                  <span className="text-sm font-bold">{tasks.lastCompleted.title}</span>
                </span>
              </>
            )}
          </div>

          {/* Main Contact */}
          <div className="flex flex-col gap-2">
            <div className="text-md font-bold">Main Contact</div>
            <div className="text-sm">
              <span className="mr-6">
                {eventExhibitor?.contactFirstName} {eventExhibitor?.contactLastName}
              </span>
              <span>{eventExhibitor?.contactEmail}</span>
            </div>

            <div className="mt-4 flex flex-col gap-4">
              <div className="flex flex-col">
                <span className="text-md font-bold">Allowed Domain</span>
                <span className="mb-2 text-xs">
                  If self-registration is enabled with the toggle below, users with email addresses
                  that match the configured domain will be allowed to self-register.
                </span>

                <TextInput
                  className="w-[450px] rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  icon={<span className="ml-2">@</span>}
                  id="allowedDomain"
                  name="allowedDomain"
                  onChange={(e) => {
                    setDomain(e.target.value)
                    setDomainError(null)
                  }}
                  onBlur={async (e) => {
                    if (eventExhibitor) {
                      // Mirrors the server-side expression: labels may not be empty or start or
                      // end with a hyphen, and the final label is the TLD.
                      const domainRegex =
                        /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/
                      const value = e.target.value.trim().toLowerCase()

                      // If the domain is not empty, verify it is a valid domain
                      if (value.length !== 0 && !domainRegex.test(value)) {
                        setDomainError('Please enter a valid domain.')
                        return
                      }

                      // If the domain is empty, set the allowed domain to null
                      const payload = { id: eventExhibitor.exhibitor.id }
                      if (value === '') {
                        payload.allowedDomain = null
                        payload.enableRegistrationByDomain = false
                      } else {
                        payload.allowedDomain = value
                      }

                      // The blocked-domain rejection (EXEC-1612) names the domain and says why,
                      // so it is worth keeping under the field as well as in the toast.
                      const updatedExhibitor = await updateOrganization(
                        payload,
                        (m) => {
                          setDomainError(m)
                          handleErrors(m)
                        },
                        () => handleSuccess('Allowed domain updated.'),
                      )

                      if (updatedExhibitor) {
                        setDomain(updatedExhibitor.allowedDomain || '')
                        setEventExhibitor({ ...eventExhibitor, exhibitor: updatedExhibitor })
                      }
                    }
                  }}
                  placeholder="domain.com"
                  value={domain}
                />

                {domainError && (
                  <div className="mt-1 w-[450px] bg-error-light px-2 py-1">
                    <p className="text-sm font-medium text-error-dark" id="domain-error">
                      {domainError}
                    </p>
                  </div>
                )}
              </div>

              <Toggle
                checked={eventExhibitor?.exhibitor.enableRegistrationByDomain}
                disabled={
                  !eventExhibitor?.exhibitor.allowedDomain ||
                  eventExhibitor?.exhibitor.allowedDomain === ''
                }
                id="enableRegistrationByDomain"
                inter
                label="Allow Self-Registration by Domain"
                labelClassName="sm:flex-none text-sm"
                onChange={async () => {
                  const updatedExhibitor = await updateOrganization(
                    {
                      id: eventExhibitor.exhibitor.id,
                      enableRegistrationByDomain:
                        !eventExhibitor.exhibitor.enableRegistrationByDomain,
                    },
                    handleErrors,
                    () => handleSuccess('Self-registration updated.'),
                  )

                  if (updatedExhibitor)
                    setEventExhibitor({ ...eventExhibitor, exhibitor: updatedExhibitor })
                }}
              />
            </div>
          </div>

          {/* KPI */}
          {kpis && (
            <div className="flex w-full flex-col space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
              <DataTile
                color="blue"
                icon={<CurrencyDollarIcon className="h-6 stroke-white" />}
                label="Total Revenue"
                value={kpis.totalRevenue.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              />

              <DataTile
                color="green"
                icon={<IdentificationIcon className="h-6 fill-white" />}
                label="Total Licenses"
                value={kpis.totalLicensesCount}
              />

              <DataTile
                color="purple"
                icon={<DevicePhoneMobileIcon className="h-6 fill-white" />}
                label="Total Devices Rented"
                value={kpis.rentalDeviceCount}
              />
            </div>
          )}

          {/* Licensed Users */}
          <div className="grid">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-md font-bold">
                {`Licensed Users: ${assignedCount}/${activeLicenses.length} Assigned`}
              </div>

              {isEEUser && (
                <div className="flex gap-2">
                  <Button
                    background="bg-white"
                    disabled={selectedLicenses.length === 0}
                    icon={<EnvelopeIcon className="h-4 stroke-gray sm:h-5" />}
                    label="Resend Emails"
                    onClick={() => setShowResendModal(true)}
                    outlined
                  />
                  {/* Opens with whatever is ticked here, but the modal lists everything
                      refundable — including unassigned licenses and rented devices, which
                      have no selection of their own */}
                  <Button
                    background="bg-white"
                    icon={<CurrencyDollarIcon className="h-4 stroke-gray sm:h-5" />}
                    label="Refund"
                    onClick={() =>
                      openRefundModal(selectedLicenses.map((license) => `license:${license.id}`))
                    }
                    outlined
                  />
                </div>
              )}
            </div>

            <DataTable
              columns={[
                {
                  id: 'name',
                  grow: 1,
                  name: 'Name',
                  selector: (row) => row.id,
                  cell: (row) => (
                    <span
                      className={`font-medium text-black ${row.isCancelled ? 'line-through' : ''}`}
                    >
                      {row.assignee.user.firstName} {row.assignee.user.lastName}
                    </span>
                  ),
                  minWidth: '200px',
                },
                {
                  id: 'email',
                  grow: 1,
                  name: 'Email',
                  selector: (row) => row.assignee,
                  cell: (row) => (
                    <span className="font-medium text-black">{row.assignee.user.email}</span>
                  ),
                  // Trimmed to claw back roughly what the Type column costs, so this
                  // table needs no more horizontal room than it did before
                  minWidth: '220px',
                },
                {
                  id: 'phone',
                  grow: 1,
                  name: 'Phone',
                  selector: (row) => row.assignee,
                  cell: (row) =>
                    row.assignee.user.phoneNumber ? (
                      <span className="font-medium text-black">
                        {row.assignee.user.phoneNumber}
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    ),
                  minWidth: '130px',
                },
                {
                  id: 'licenseId',
                  grow: 1,
                  name: 'License Name',
                  selector: (row) => row.assignee,
                  cell: (row) => <span className="font-medium text-black">{row.licenseId}</span>,
                  minWidth: '115px',
                },
                typeColumn,
                {
                  id: 'status',
                  grow: 0.5,
                  name: 'Status',
                  cell: (row) => <LicenseStatusBadge status={row.status} />,
                  // Wide enough for the longest derived status, "Refunded + Cancelled"
                  minWidth: '170px',
                },
                actionColumn(
                  'qr',
                  (row) =>
                    !row.isCancelled && (
                      <button
                        className={ROW_ACTION_CLASS}
                        onClick={() => setQrLicense(row)}
                        type="button"
                      >
                        <QRIcon className="h-4 shrink-0 fill-purple" />
                        QR
                      </button>
                    ),
                  '90px',
                ),
                actionColumn(
                  'reassign',
                  (row) =>
                    isEEUser &&
                    !row.isCancelled && (
                      <button
                        className={ROW_ACTION_CLASS}
                        onClick={() => {
                          setAssignSearchTerm('')
                          setAssignee({
                            label: `${row.assignee.user.firstName} ${row.assignee.user.lastName}`,
                            id: row.assignee.id,
                            phoneNumber: row.assignee.user.phoneNumber,
                          })
                          setLicenseToAssign(row)
                        }}
                        type="button"
                      >
                        <EditIcon className="h-4 shrink-0 fill-purple" />
                        Reassign
                      </button>
                    ),
                  '140px',
                ),
                actionColumn('refund', renderRefundAction, '125px'),
                actionColumn('cancel', renderCancelAction, '120px'),
              ]}
              data={assignedLicenses}
              defaultSortFieldId="name"
              defaultSortAsc
              clearSelectedRows={clearSelectedRows}
              onSelectedRowsChange={({ selectedRows }) => setSelectedLicenses(selectedRows)}
              selectableRows={isEEUser}
              selectableRowDisabled={(row) => row.isCancelled}
              onChangePage={async (page) => {
                setAssignedPage(page)
                await getAssignedLicenses(licensesPageUrl(true, assignedPerPage, page))
              }}
              onChangeRowsPerPage={async (rowsPerPage) => {
                setAssignedPerPage(rowsPerPage)
                setAssignedPage(1)
                await getAssignedLicenses(licensesPageUrl(true, rowsPerPage, 1))
              }}
              pagination
              paginationDefaultPage={assignedPage}
              paginationPerPage={assignedPerPage}
              paginationRowsPerPageOptions={[5, 10, 15, 20, 30, 50]}
              paginationTotalRows={assignedTotal}
              paginationServer
              progressPending={loadingAssigned}
              sortServer
            />
          </div>

          {/* Unassigned Licenses */}
          <div className="grid">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-md font-bold">
                {`Available Licenses: ${unassignedCount}/${activeLicenses.length} Unassigned`}
              </div>
            </div>

            <DataTable
              columns={[
                {
                  id: 'licenseId',
                  grow: 1,
                  name: 'License Name',
                  selector: (row) => row.licenseId,
                  cell: (row) => (
                    <span
                      className={`font-medium text-black ${row.isCancelled ? 'line-through' : ''}`}
                    >
                      {row.licenseId}
                    </span>
                  ),
                  minWidth: '200px',
                },
                typeColumn,
                {
                  id: 'status',
                  grow: 0.5,
                  name: 'Status',
                  cell: (row) => <LicenseStatusBadge status={row.status} />,
                  minWidth: '170px',
                },
                actionColumn(
                  'assign',
                  (row) =>
                    isEEUser &&
                    !row.isCancelled && (
                      <button
                        className={ROW_ACTION_CLASS}
                        onClick={() => {
                          setAssignSearchTerm('')
                          setAssignee(null)
                          setLicenseToAssign(row)
                        }}
                        type="button"
                      >
                        <PlusIcon className="h-4 shrink-0 stroke-purple" />
                        Assign
                      </button>
                    ),
                  '120px',
                ),
                actionColumn('refund', renderRefundAction, '125px'),
                actionColumn('cancel', renderCancelAction, '120px'),
              ]}
              data={unassignedLicenses}
              defaultSortFieldId="licenseId"
              defaultSortAsc
              noDataComponent={
                <span className="py-6 text-sm text-gray-600">
                  Every license for this exhibitor is assigned.
                </span>
              }
              onChangePage={async (page) => {
                setUnassignedPage(page)
                await getUnassignedLicenses(licensesPageUrl(false, unassignedPerPage, page))
              }}
              onChangeRowsPerPage={async (rowsPerPage) => {
                setUnassignedPerPage(rowsPerPage)
                setUnassignedPage(1)
                await getUnassignedLicenses(licensesPageUrl(false, rowsPerPage, 1))
              }}
              pagination
              paginationDefaultPage={unassignedPage}
              paginationPerPage={unassignedPerPage}
              paginationRowsPerPageOptions={[5, 10, 15, 20, 30, 50]}
              paginationTotalRows={unassignedTotal}
              paginationServer
              progressPending={loadingUnassigned}
            />
          </div>

          {/* Rented Devices — refunded through the same action as licenses, since the
              two are bought on one order and come back on one credit (EXEC-1604) */}
          {isEEUser && rentalDevices.length > 0 && (
            <div className="grid">
              <div className="text-md mb-2 font-bold">
                {/* "Rented" matches the KPI tile: a cancelled or refunded rental has
                    gone back, so it no longer counts (EXEC-1604) */}
                {`Rented Devices: ${
                  rentalDevices.filter((d) => !d.isCancelled && !d.isRefunded).length
                }/${rentalDevices.length} Rented`}
              </div>

              <DataTable
                columns={[
                  {
                    id: 'device',
                    grow: 2,
                    name: 'Device',
                    selector: (row) => row.id,
                    cell: (_row, index) => `Rental Device ${index + 1}`,
                    minWidth: '160px',
                  },
                  {
                    id: 'orderNumber',
                    grow: 1,
                    name: 'Order',
                    selector: (row) => row.orderNumber,
                    cell: (row) => row.orderNumber || '—',
                    minWidth: '120px',
                  },
                  {
                    id: 'purchasePrice',
                    grow: 1,
                    name: 'Amount Paid',
                    selector: (row) => row.purchasePrice,
                    cell: (row) => `$${row.purchasePrice}`,
                    minWidth: '120px',
                  },
                  {
                    id: 'status',
                    grow: 1,
                    name: 'Status',
                    selector: (row) => row.status,
                    cell: (row) => <LicenseStatusBadge status={row.status} />,
                    minWidth: '140px',
                  },
                  actionColumn(
                    'refund',
                    (row) =>
                      !row.isRefunded && (
                        <button
                          className={ROW_ACTION_CLASS}
                          onClick={() => openRefundModal([`device:${row.id}`])}
                          type="button"
                        >
                          <CurrencyDollarIcon className="h-4 shrink-0 stroke-purple" />
                          Refund
                        </button>
                      ),
                    '125px',
                  ),
                  actionColumn(
                    'cancel',
                    (row) =>
                      !row.isCancelled && (
                        <button
                          className={ROW_ACTION_DESTRUCTIVE_CLASS}
                          onClick={() => setDeviceToCancel(row)}
                          type="button"
                        >
                          <XCircleIcon className="h-4 shrink-0 stroke-error-dark" />
                          Cancel
                        </button>
                      ),
                    '120px',
                  ),
                ]}
                data={rentalDevices}
                progressPending={loadingRentalDevices}
              />
            </div>
          )}

          {/* Purchases — the same receipts the exhibitor admin can download for
              themselves, so EE staff can pull one up on a support call */}
          {event && eventExhibitor && (
            <PurchaseReceipts
              event={event}
              eventExhibitorId={eventExhibitorId}
              organizationName={eventExhibitor.exhibitor?.name}
            />
          )}
        </div>
      </StateContainer>

      {showExhibitorModal && (
        <AddExhibitorModal
          editExhibitor={editExhibitor}
          loading={loadingExhibitor}
          onSubmit={onSubmit}
          setShowExhibitorModal={setShowExhibitorModal}
        />
      )}

      {/* Waits for the exhibitor, because the modal prefills the adjusted unit price once
          on mount and never revisits it. `loadingExhibitor` starts false, so the Purchase
          button paints before the first fetch lands; opening the modal in that window
          prefilled the first-license price for an exhibitor who had already bought one. */}
      {showPurchaseModal && eventExhibitor && (
        <PurchaseLicensesModal
          adjustablePricing
          event={event}
          heldLicenseCount={kpis?.totalLicensesCount || 0}
          purchasedLicenseCount={eventExhibitor?.licenseCounts?.numValidPaidLicenses || 0}
          loading={loadingPurchase}
          onSubmit={submitPurchasingForm}
          showModal={showPurchaseModal}
          setShowModal={setShowPurchaseModal}
        />
      )}

      {repeatPurchaseDialog}

      {qrLicense && (
        <LicenseQRModal
          license={qrLicense}
          showModal={!!qrLicense}
          setShowModal={() => setQrLicense(null)}
        />
      )}

      {/* Stood down while the phone prompt is up, so the two don't stack */}
      {licenseToAssign && !pendingAssigneeId && (
        <AssignLicenseModal
          assignee={assignee}
          license={licenseToAssign}
          loading={loadingAssignableUsers || updatingLicense}
          onSubmit={submitAssignment}
          options={assignableUsers}
          setAssignee={setAssignee}
          setSearchTerm={debounceAssignSearch}
          setShowModal={() => setLicenseToAssign(null)}
          showModal={!!licenseToAssign}
        />
      )}

      {/* EXEC-1608: closes the hole that produces the empty phone cells EXEC-1598 added
          an empty state for. Keeps the exhibitor side's Skip — EE shouldn't be stuck when
          a booth staffer genuinely has no number to give. */}
      {pendingAssigneeId && (
        <Modal
          actions={[
            {
              type: 'cancel',
              label: 'Skip',
              onClick: () => performAssignment(pendingAssigneeId),
            },
            {
              type: 'submit',
              label: 'Save & Assign',
              onClick: handlePhoneSubmit((data) =>
                performAssignment(pendingAssigneeId, data.phoneNumber),
              ),
            },
          ]}
          icon={<ExclamationTriangleIcon className="h-6 stroke-white" />}
          content={
            <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
              <p className="text-left font-nunito">
                <span className="font-bold">{assignee?.label}</span> does not have a phone number
                set. Enter their phone number below, or skip to assign without one.
              </p>

              <PhoneNumberInput
                control={phoneControl}
                disabled={updatingLicense}
                error={phoneErrors.phoneNumber}
                label="Phone Number"
                name="phoneNumber"
                placeholder="Phone Number"
                required
              />
            </div>
          }
          loading={updatingLicense}
          onClose={() => {
            setPendingAssigneeId(null)
            resetPhone({ phoneNumber: '' })
          }}
          open={!!pendingAssigneeId}
          setOpen={() => setPendingAssigneeId(null)}
          title="Add Phone Number"
        />
      )}

      {showResendModal && (
        <ResendLicenseEmailsModal
          licenses={selectedLicenses}
          loading={loadingResend}
          onSubmit={submitResendEmails}
          showModal={showResendModal}
          setShowModal={setShowResendModal}
        />
      )}

      {licenseToResolve && (
        <Dialog
          loading={resolvingLicense}
          message={resolveLicenseMessage(licenseToResolve)}
          onCancel={() => setLicenseToResolve(null)}
          onConfirm={resolveLicense}
          title="Cancel this license?"
          type="warning"
        />
      )}

      {deviceToCancel && (
        <Dialog
          loading={cancellingDevice}
          message="This rental device will stop counting towards the exhibitor's rentals. This doesn't refund anything."
          onCancel={() => setDeviceToCancel(null)}
          onConfirm={resolveDeviceCancel}
          title="Cancel this rental device?"
          type="warning"
        />
      )}

      {showRefundModal && (
        <RefundModal
          event={event}
          eventExhibitorId={eventExhibitorId}
          initialSelection={refundSelection}
          licenses={licenses}
          loading={issuingRefund}
          onSubmit={submitRefund}
          rentalDevices={rentalDevices}
          setShowModal={setShowRefundModal}
          showModal={showRefundModal}
        />
      )}
    </div>
  )
})

export default EventExhibitorDetail
